import * as React from "react";

import { Headings } from "@project-stories/elements/Headings/Headings";
import { Paragraph } from "@arctheme-components/elements/Paragraph/Paragraph";
import useMobileBreakpoint from "@src/hooks/useMobileBreakpoint";
import { mobileBreakpoint } from "../../../../entrypoints/theme";

export const Title = ({ settings }) => {
  const isMobile = useMobileBreakpoint(mobileBreakpoint);

  // Auto-detect if this should be an H1 based on page context
  const shouldUseH1 = () => {
    const title = settings.block_settings?.title;
    const blockType = settings.block_type;
    const sectionType = settings.section_settings?.type;
    const sectionClass = settings.section_settings?.class;
    const blockId = settings.id;

    // Primary detection: Check if this is a main title block type
    if (blockType === 'product_title' ||
        blockType === 'collection_title' ||
        blockType === 'page_title' ||
        blockType === 'article_title') {
      return true;
    }
    
    // Secondary detection: Check for Shopify liquid variables that indicate main page titles
    if (title?.includes('{{ product.title }}') || 
        title?.includes('{{ collection.title }}') || 
        title?.includes('{{ page.title }}') ||
        title?.includes('{{ article.title }}') ||
        title?.includes('product.metafields.ap21.matchingKitName')) {
      return true;
    }
    
    // Tertiary detection: Check if this is in a main product/collection/page section
    if (sectionType === 'main-product' || 
        sectionType === 'main-collection' || 
        sectionType === 'main-page' ||
        sectionType === 'main-article') {
      return true;
    }
    
    // Check section class for main sections
    if (sectionClass === 'section-main-product' ||
        sectionClass === 'section-main-collection' ||
        sectionClass === 'section-main-page' ||
        sectionClass === 'section-main-article') {
      return true;
    }
    
    // Fallback: Check if this is a product_title block ID (used on product pages)
    if (blockId?.includes('product_title')) {
      return true;
    }
    
    return false;
  };

  // Helper function to extract valid HTML tag and CSS classes
  const parseHeadingConfig = (configValue) => {
    const validTags = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];
    
    if (!configValue) {
      return { tag: 'h2', classes: '' };
    }
    
    // If it's already a valid tag, use it
    if (validTags.includes(configValue)) {
      return { tag: configValue, classes: '' };
    }
    
    // If it contains spaces, it's likely CSS classes, default to h2 for tag
    if (configValue.includes(' ')) {
      return { tag: 'h2', classes: configValue };
    }
    
    // If it's a single word but not a valid tag, treat as CSS class
    if (!validTags.includes(configValue)) {
      return { tag: 'h2', classes: configValue };
    }
    
    return { tag: configValue, classes: '' };
  };

  const renderComponents = () => {
    return (
      <div
        className={`block-title mt-[${settings.block_settings.margin_top_mobile}px] lg:mt-[${settings.block_settings.margin_top_desktop}px] mb-[${settings.block_settings.margin_bottom_mobile}px] lg:mb-[${settings.block_settings.margin_bottom_desktop}px] text-${settings.section_settings.content_alignment_mobile} lg:text-${settings.section_settings.content_alignment_desktop}`}
      >
        {isMobile ? (
          (() => {
            const mobileConfig = parseHeadingConfig(settings.block_settings.mobile_class || 'h2');
            return shouldUseH1() ? (
              <Headings
                text={settings.block_settings.title}
                type="h1"
                classes={mobileConfig.classes || mobileConfig.tag}
              />
            ) : (
              <Headings
                text={settings.block_settings.title}
                type={mobileConfig.tag}
                classes={mobileConfig.classes}
              />
            );
          })()
        ) : (
          (() => {
            const desktopConfig = parseHeadingConfig(settings.block_settings.heading_type || 'h3');
            return shouldUseH1() ? (
              <Headings
                text={settings.block_settings.title}
                type="h1"
                classes={desktopConfig.classes || desktopConfig.tag}
              />
            ) : (
              <Headings
                text={settings.block_settings.title}
                type={desktopConfig.tag}
                classes={desktopConfig.classes}
              />
            );
          })()
        )}
      </div>
    );
  };

  if (settings.block_settings.render_type !== "all") {
    if (isMobile && settings.block_settings.render_type !== "mobile-only")
      return;
    if (!isMobile && settings.block_settings.render_type !== "desktop-only")
      return;
  }

  return <>{renderComponents()}</>;
};

export default Title;
